/**
 * ProgressRing — cinematic SVG radial progress with animated stroke.
 *
 * Props:
 *   size      — px (default 120)
 *   stroke    — px (default 8)
 *   value     — 0..100
 *   color     — stroke colour (default var(--primary))
 *   trackColor — the un-filled arc colour
 *   label     — small label above the number
 *   children  — inner content (overrides default % label)
 *   glow      — bool, adds a pulse glow filter
 */
export default function ProgressRing({
  size = 120,
  stroke = 8,
  value = 0,
  color = "var(--primary)",
  trackColor = "rgba(255,255,255,0.08)",
  label,
  children,
  glow = false,
  ariaLabel,
}) {
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - clamped / 100);

  return (
    <div
      className={`relative inline-flex items-center justify-center ${glow ? "hud-pulse" : ""}`}
      style={{ width: size, height: size }}
      aria-label={ariaLabel || label}
      role="img"
    >
      <svg width={size} height={size} className="rotate-[-90deg]">
        <defs>
          <linearGradient id={`pg-${size}-${stroke}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.85" />
            <stop offset="100%" stopColor={color} stopOpacity="1" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={`url(#pg-${size}-${stroke})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 720ms cubic-bezier(0.16, 1, 0.3, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {children != null ? (
          children
        ) : (
          <>
            {label && (
              <div className="text-[9px] uppercase tracking-widest text-muted font-black leading-none mb-1">
                {label}
              </div>
            )}
            <div className="font-display font-black tabular-nums leading-none" style={{ fontSize: size * 0.28, color }}>
              {Math.round(clamped)}
              <span className="text-[0.5em] text-muted">%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
